import * as THREE from "three";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";

export interface ExportGLBOptions {
  binary?: boolean;
}

/**
 * Export one or more three.js objects as a binary GLB ArrayBuffer.
 *
 * IMPORTANT: pass the ACTUAL top-level nodes you want to appear under the
 * glTF scene — do NOT pass the `gltf.scene` wrapper Group that GLTFLoader
 * returns. Passing the wrapper would cause GLTFExporter to emit an extra
 * root node ("Scene") wrapping the real root (e.g. your `Armature.001`),
 * which changes the file's topology vs. the original.
 *
 * Correct usage, matching the original DCL wearable structure:
 *
 *   exportGLB(gltf.scene.children)   // => scene → Armature.001 → ...
 *
 * Incorrect (what we used to do):
 *
 *   exportGLB(gltf.scene)            // => scene → Scene → Armature.001 → ...
 *
 * The caller should make sure any CanvasTexture used by the object's
 * materials has been marked `needsUpdate = true` beforehand so the
 * exporter picks up the latest canvas pixels.
 */
export function exportGLB(
  roots: THREE.Object3D | THREE.Object3D[],
  options: ExportGLBOptions = {},
): Promise<ArrayBuffer> {
  const exporter = new GLTFExporter();
  const binary = options.binary ?? true;

  // GLTFExporter.parse accepts either a single Object3D or an array of
  // Object3Ds. When an array is passed, each element becomes a top-level
  // node of the exported scene — which is exactly what we want.
  const input = Array.isArray(roots) ? roots : [roots];

  return new Promise((resolve, reject) => {
    exporter.parse(
      input,
      (result) => {
        if (result instanceof ArrayBuffer) {
          resolve(result);
        } else {
          // Non-binary JSON result; serialize to ArrayBuffer.
          const json = JSON.stringify(result);
          const enc = new TextEncoder().encode(json);
          resolve(enc.buffer);
        }
      },
      (err) => reject(err instanceof Error ? err : new Error(String(err))),
      {
        binary,
        embedImages: true,
        includeCustomExtensions: false,
        // trs: true  => write per-node translation/rotation/scale components
        // (matching DCL's source format) rather than a single 16-float
        // matrix. Keeps node transforms readable and closer to the
        // original export from Blender's glTF I/O.
        trs: true,
        // Include everything — bone nodes are already `visible: true` but
        // being explicit ensures we never silently drop skeleton parts.
        onlyVisible: false,
      },
    );
  });
}
